import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Calendar,
  CalendarClock,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Search,
  RefreshCw,
  Phone,
  ArrowRight,
  Filter,
  User,
  Stethoscope,
  FileText,
  ChevronRight,
  Plus,
  Edit3,
  ExternalLink,
  MessageSquare,
  Sparkles,
  CalendarCheck,
  ChevronDown,
  X,
  Pill,
  Activity,
  Check
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  getAppointmentFollowUps,
  getAppointmentFollowUpStatistics,
  updateAppointmentFollowUpStatus
} from '../../services/appointmentApi';

const AppointmentFollowUpList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [followUps, setFollowUps] = useState([]);
  const [stats, setStats] = useState({
    totalFollowUps: 0,
    dueToday: 0,
    overdue: 0,
    upcoming: 0,
    completed: 0
  });
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  // Filter States
  const currentTab = searchParams.get('tab') || 'all';
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedDoctor, setSelectedDoctor] = useState(searchParams.get('doctorId') || '');
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');

  // Modal State for updating follow-up
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: 'completed',
    notes: '',
    targetDate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch doctors for filter dropdown
  useEffect(() => {
    const fetchDoctorsList = async () => {
      try {
        const res = await api.get('/doctors?limit=100');
        const list = res.data?.data?.doctors || res.data?.doctors || res.data?.data || [];
        setDoctors(list);
      } catch (err) {
        console.error('Failed to load doctors list', err);
      }
    };
    fetchDoctorsList();
  }, []);

  // Fetch follow-ups list
  const fetchFollowUpsData = useCallback(async () => {
    setLoading(true);
    try {
      const page = parseInt(searchParams.get('page') || '1', 10);
      const params = {
        page,
        limit: 20,
        status: currentTab,
        search: searchTerm,
        doctorId: selectedDoctor,
        startDate,
        endDate
      };

      const res = await getAppointmentFollowUps(params);
      if (res?.success && res.data) {
        setFollowUps(res.data.followUps || []);
        setPagination(res.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error('Failed to fetch appointment follow-ups', err);
      toast.error('Failed to load appointment follow-ups');
    } finally {
      setLoading(false);
    }
  }, [currentTab, searchTerm, selectedDoctor, startDate, endDate, searchParams]);

  // Fetch statistics summary
  const fetchStatsData = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getAppointmentFollowUpStatistics();
      if (res?.success && res.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Failed to load follow-up stats', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollowUpsData();
    fetchStatsData();
  }, [fetchFollowUpsData, fetchStatsData]);

  // Tab change handler
  const handleTabChange = (tabKey) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tabKey === 'all') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabKey);
    }
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  // Search input handler with debounce
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const nextParams = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      nextParams.set('search', searchTerm.trim());
    } else {
      nextParams.delete('search');
    }
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  // Open Update Modal
  const openUpdateModal = (item) => {
    setSelectedFollowUp(item);
    setUpdateForm({
      status: item.rawFollowUpStatus === 'completed' ? 'completed' : 'completed',
      notes: item.followUpNotes || '',
      targetDate: item.followUpTargetDate ? new Date(item.followUpTargetDate).toISOString().split('T')[0] : ''
    });
    setIsUpdateModalOpen(true);
  };

  // Submit Update
  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    if (!selectedFollowUp) return;

    setSubmitting(true);
    try {
      await updateAppointmentFollowUpStatus(selectedFollowUp.prescriptionId, updateForm);
      toast.success(
        updateForm.status === 'completed'
          ? 'Follow-up marked as completed!'
          : 'Follow-up updated successfully'
      );
      setIsUpdateModalOpen(false);
      fetchFollowUpsData();
      fetchStatsData();
    } catch (err) {
      console.error('Failed to update follow-up', err);
      toast.error(err.response?.data?.message || 'Failed to update follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  // Format Helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? 'N/A'
      : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* ── 1. Top Header & Breadcrumbs ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
            <Link to="/appointments" className="hover:text-[#143E78] transition-colors">
              Appointments
            </Link>
            <ChevronRight size={12} />
            <span className="text-[#143E78] font-black">Follow-up Queue</span>
          </div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              Appointment Follow-up Queue
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200/80">
              Prescription Care
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
            Track and schedule consultations for patients whose prescriptions contain follow-up instructions (e.g., 3 days, 5 days, 1 week).
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <button
            onClick={() => {
              fetchFollowUpsData();
              fetchStatsData();
              toast.success('Refreshed follow-up queue');
            }}
            className="p-2.5 text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all cursor-pointer shadow-2xs"
            title="Refresh list"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <Link
            to="/appointments"
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all shadow-2xs"
          >
            <Calendar size={15} className="text-slate-500" />
            <span>All Appointments</span>
          </Link>
        </div>
      </div>

      {/* ── 2. KPI Metrics Ribbon ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {/* Total */}
        <div
          onClick={() => handleTabChange('all')}
          className={`p-4 rounded-2xl border transition-all cursor-pointer ${
            currentTab === 'all'
              ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20'
              : 'bg-white text-slate-800 border-slate-200/80 hover:border-blue-300 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${currentTab === 'all' ? 'text-blue-100' : 'text-slate-400'}`}>
              Total Follow-ups
            </span>
            <CalendarCheck size={18} className={currentTab === 'all' ? 'text-white' : 'text-blue-600'} />
          </div>
          <div className="text-2xl font-black mt-2">
            {statsLoading ? '...' : (stats.totalFollowUps || 0)}
          </div>
          <span className={`text-[10px] font-bold mt-0.5 block ${currentTab === 'all' ? 'text-blue-200' : 'text-slate-400'}`}>
            Prescriptions with follow-up
          </span>
        </div>

        {/* Due Today */}
        <div
          onClick={() => handleTabChange('due_today')}
          className={`p-4 rounded-2xl border transition-all cursor-pointer ${
            currentTab === 'due_today'
              ? 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-500/20'
              : 'bg-white text-slate-800 border-slate-200/80 hover:border-amber-300 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${currentTab === 'due_today' ? 'text-amber-100' : 'text-amber-600'}`}>
              Due Today
            </span>
            <Flame size={18} className={currentTab === 'due_today' ? 'text-white' : 'text-amber-500'} />
          </div>
          <div className="text-2xl font-black mt-2">
            {statsLoading ? '...' : (stats.dueToday || 0)}
          </div>
          <span className={`text-[10px] font-bold mt-0.5 block ${currentTab === 'due_today' ? 'text-amber-100' : 'text-amber-600'}`}>
            Requires immediate contact
          </span>
        </div>

        {/* Overdue */}
        <div
          onClick={() => handleTabChange('overdue')}
          className={`p-4 rounded-2xl border transition-all cursor-pointer ${
            currentTab === 'overdue'
              ? 'bg-rose-600 text-white border-rose-600 shadow-md shadow-rose-500/20'
              : 'bg-white text-slate-800 border-slate-200/80 hover:border-rose-300 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${currentTab === 'overdue' ? 'text-rose-100' : 'text-rose-600'}`}>
              Overdue
            </span>
            <AlertTriangle size={18} className={currentTab === 'overdue' ? 'text-white' : 'text-rose-500'} />
          </div>
          <div className="text-2xl font-black mt-2">
            {statsLoading ? '...' : (stats.overdue || 0)}
          </div>
          <span className={`text-[10px] font-bold mt-0.5 block ${currentTab === 'overdue' ? 'text-rose-200' : 'text-rose-600'}`}>
            Past target review date
          </span>
        </div>

        {/* Upcoming */}
        <div
          onClick={() => handleTabChange('upcoming')}
          className={`p-4 rounded-2xl border transition-all cursor-pointer ${
            currentTab === 'upcoming'
              ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
              : 'bg-white text-slate-800 border-slate-200/80 hover:border-indigo-300 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${currentTab === 'upcoming' ? 'text-indigo-100' : 'text-slate-400'}`}>
              Upcoming
            </span>
            <CalendarClock size={18} className={currentTab === 'upcoming' ? 'text-white' : 'text-indigo-600'} />
          </div>
          <div className="text-2xl font-black mt-2">
            {statsLoading ? '...' : (stats.upcoming || 0)}
          </div>
          <span className={`text-[10px] font-bold mt-0.5 block ${currentTab === 'upcoming' ? 'text-indigo-200' : 'text-slate-400'}`}>
            Scheduled in future days
          </span>
        </div>

        {/* Completed */}
        <div
          onClick={() => handleTabChange('completed')}
          className={`p-4 rounded-2xl border transition-all cursor-pointer col-span-2 sm:col-span-1 ${
            currentTab === 'completed'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-500/20'
              : 'bg-white text-slate-800 border-slate-200/80 hover:border-emerald-300 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-[11px] font-black uppercase tracking-wider ${currentTab === 'completed' ? 'text-emerald-100' : 'text-emerald-700'}`}>
              Completed
            </span>
            <CheckCircle2 size={18} className={currentTab === 'completed' ? 'text-white' : 'text-emerald-600'} />
          </div>
          <div className="text-2xl font-black mt-2">
            {statsLoading ? '...' : (stats.completed || 0)}
          </div>
          <span className={`text-[10px] font-bold mt-0.5 block ${currentTab === 'completed' ? 'text-emerald-200' : 'text-emerald-600'}`}>
            Follow-up closed & reviewed
          </span>
        </div>
      </div>

      {/* ── 3. Filter Bar & Search ── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs space-y-3.5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
            {[
              { key: 'all', label: 'All Follow-ups', count: stats.totalFollowUps },
              { key: 'due_today', label: 'Due Today', count: stats.dueToday, isAlert: stats.dueToday > 0 },
              { key: 'overdue', label: 'Overdue', count: stats.overdue, isDanger: stats.overdue > 0 },
              { key: 'upcoming', label: 'Upcoming', count: stats.upcoming },
              { key: 'completed', label: 'Completed', count: stats.completed }
            ].map((tab) => {
              const active = currentTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    active
                      ? 'bg-[#143E78] text-white shadow-2xs'
                      : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${
                      active
                        ? 'bg-white/20 text-white'
                        : tab.isDanger
                        ? 'bg-rose-100 text-rose-700'
                        : tab.isAlert
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {tab.count ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 min-w-[280px]">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search patient, phone, booking ID, doctor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:bg-white focus:border-[#143E78] focus:outline-none transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('search');
                    setSearchParams(nextParams);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-3.5 py-2 bg-[#143E78] text-white rounded-xl text-xs font-bold hover:bg-[#0f2c56] transition-all cursor-pointer shadow-2xs"
            >
              Search
            </button>
          </form>
        </div>

        {/* Doctor & Date Filters */}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
            <Filter size={13} className="text-slate-400" />
            <span>Filter By:</span>
          </div>

          {/* Doctor Filter */}
          <select
            value={selectedDoctor}
            onChange={(e) => {
              setSelectedDoctor(e.target.value);
              const nextParams = new URLSearchParams(searchParams);
              if (e.target.value) nextParams.set('doctorId', e.target.value);
              else nextParams.delete('doctorId');
              nextParams.set('page', '1');
              setSearchParams(nextParams);
            }}
            className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:border-[#143E78]"
          >
            <option value="">All Doctors</option>
            {doctors.map((doc) => (
              <option key={doc._id} value={doc._id}>
                Dr. {doc.user?.firstName} {doc.user?.lastName} ({doc.specialization || 'Consultant'})
              </option>
            ))}
          </select>

          {/* Date range inputs */}
          <div className="flex items-center gap-1.5 text-xs">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                const nextParams = new URLSearchParams(searchParams);
                if (e.target.value) nextParams.set('startDate', e.target.value);
                else nextParams.delete('startDate');
                nextParams.set('page', '1');
                setSearchParams(nextParams);
              }}
              className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none"
              title="Filter from target date"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                const nextParams = new URLSearchParams(searchParams);
                if (e.target.value) nextParams.set('endDate', e.target.value);
                else nextParams.delete('endDate');
                nextParams.set('page', '1');
                setSearchParams(nextParams);
              }}
              className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none"
              title="Filter to target date"
            />
          </div>

          {(selectedDoctor || startDate || endDate || searchTerm) && (
            <button
              onClick={() => {
                setSelectedDoctor('');
                setStartDate('');
                setEndDate('');
                setSearchTerm('');
                const nextParams = new URLSearchParams();
                if (currentTab !== 'all') nextParams.set('tab', currentTab);
                setSearchParams(nextParams);
              }}
              className="text-[11px] font-bold text-rose-600 hover:text-rose-800 underline ml-auto cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* ── 4. Main Follow-up Queue Cards / Table ── */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-2xs space-y-3">
          <div className="w-10 h-10 border-4 border-[#143E78] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-bold text-slate-600">Loading appointment follow-up queue...</p>
        </div>
      ) : followUps.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center shadow-2xs space-y-3">
          <div className="w-14 h-14 bg-blue-50 text-[#143E78] rounded-2xl flex items-center justify-center mx-auto shadow-2xs">
            <CalendarCheck size={28} />
          </div>
          <h3 className="text-base font-black text-slate-900">No Appointment Follow-ups Found</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            {currentTab === 'due_today'
              ? 'Great news! There are no follow-ups due today.'
              : currentTab === 'overdue'
              ? 'Awesome! No follow-up consultations are overdue.'
              : 'No patient prescriptions currently match the selected follow-up filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3.5">
          {followUps.map((item) => {
            const patientName = item.lead?.patientName || item.appointment?.leadId?.patientName || item.appointment?.patientName || 'Unknown Patient';
            const patientPhone = item.lead?.patientPhone || item.appointment?.leadId?.patientPhone || item.appointment?.patientPhone || '';
            const doctorName = item.doctor?.user?.firstName
              ? `Dr. ${item.doctor.user.firstName} ${item.doctor.user.lastName || ''}`
              : 'Assigned Doctor';
            const doctorSpec = item.doctor?.specialization || 'General Consultation';
            const originalDate = item.appointment?.appointmentDate;
            const isCompleted = item.rawFollowUpStatus === 'completed';
            const targetDateStr = formatDate(item.followUpTargetDate);

            return (
              <div
                key={item._id}
                className={`bg-white rounded-2xl border p-4 sm:p-5 transition-all shadow-2xs hover:shadow-md ${
                  isCompleted
                    ? 'border-emerald-200 bg-emerald-50/20'
                    : item.urgency === 'overdue'
                    ? 'border-rose-300 bg-rose-50/20'
                    : item.urgency === 'today'
                    ? 'border-amber-300 bg-amber-50/20'
                    : 'border-slate-200/90'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: Patient & Consultation Info */}
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-black text-slate-900 tracking-tight">
                        {patientName}
                      </h3>
                      {item.lead?.patientGender && (
                        <span className="text-[11px] font-bold text-slate-400">
                          • {item.lead.patientGender} {item.lead.patientAge ? `(${item.lead.patientAge}y)` : ''}
                        </span>
                      )}
                      {item.lead?.city && (
                        <span className="text-[11px] font-bold text-slate-400">
                          • {item.lead.city}
                        </span>
                      )}

                      {/* Urgency Badge */}
                      {isCompleted ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1">
                          <Check size={11} /> Completed
                        </span>
                      ) : item.urgency === 'overdue' ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300 inline-flex items-center gap-1 animate-pulse">
                          <AlertTriangle size={11} /> {Math.abs(item.daysDiff)} Days Overdue
                        </span>
                      ) : item.urgency === 'today' ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1">
                          <Flame size={11} /> Due Today
                        </span>
                      ) : item.urgency === 'soon' ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 inline-flex items-center gap-1">
                          <Clock size={11} /> Due in {item.daysDiff} Days
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                          Target: {targetDateStr}
                        </span>
                      )}
                    </div>

                    {/* Meta info row: Phone, Doctor, Original Consultation */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 font-medium">
                      {patientPhone && (
                        <span className="inline-flex items-center gap-1 font-mono font-bold text-slate-700">
                          <Phone size={12} className="text-slate-400" />
                          {patientPhone}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-slate-700 font-bold">
                        <Stethoscope size={12} className="text-[#143E78]" />
                        {doctorName} ({doctorSpec})
                      </span>
                      {originalDate && (
                        <span className="text-slate-500">
                          Consultation: <strong className="text-slate-700">{formatDate(originalDate)}</strong>
                        </span>
                      )}
                      {item.appointment?.bookingId && (
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono font-bold text-slate-600">
                          {item.appointment.bookingId}
                        </span>
                      )}
                    </div>

                    {/* Doctor's Follow-up Instructions Bubble */}
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/70 mt-2 flex items-start gap-2.5">
                      <CalendarClock size={16} className="text-[#143E78] shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                            Doctor Follow-up Schedule & Advice
                          </span>
                          <span className="text-[11px] font-bold text-[#143E78]">
                            Target Date: <strong>{targetDateStr}</strong>
                          </span>
                        </div>
                        <p className="text-xs font-bold text-slate-800 mt-0.5">
                          "{item.followUp}"
                        </p>
                      </div>
                    </div>

                    {/* Prescribed Items summary */}
                    <div className="flex items-center gap-3 pt-1 text-[11px] text-slate-500 font-medium">
                      {item.medicationsCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Pill size={12} className="text-blue-500" />
                          {item.medicationsCount} Medication{item.medicationsCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {item.investigationsCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Activity size={12} className="text-emerald-500" />
                          {item.investigationsCount} Test{item.investigationsCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {item.followUpNotes && (
                        <span className="text-slate-600 font-bold truncate max-w-sm" title={item.followUpNotes}>
                          📝 Note: {item.followUpNotes}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: Quick Action Buttons */}
                  <div className="flex flex-wrap lg:flex-col items-center lg:items-end justify-end gap-2 shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-100">
                    {/* 1-Click Book Follow-up Consultation */}
                    <button
                      onClick={() => {
                        const targetLeadId = item.lead?._id || item.lead;
                        const docId = item.doctor?._id || item.doctor;
                        navigate(
                          `/appointments/create?fromLeadId=${targetLeadId}&appointmentType=follow_up&doctorId=${docId || ''}&origin=followups`
                        );
                      }}
                      className="px-3.5 py-2 bg-[#143E78] text-white hover:bg-[#0f2c56] rounded-xl text-xs font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 shadow-2xs cursor-pointer"
                    >
                      <Plus size={13} />
                      <span>Book Follow-up</span>
                    </button>

                    <div className="flex items-center gap-1.5">
                      {/* Call Button */}
                      {patientPhone && (
                        <a
                          href={`tel:${patientPhone}`}
                          className="p-2 text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-emerald-50 rounded-xl transition-all shadow-2xs"
                          title="Call patient"
                        >
                          <Phone size={14} />
                        </a>
                      )}

                      {/* View Prescription */}
                      <Link
                        to={`/prescriptions/${item.prescriptionId}`}
                        className="px-2.5 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all shadow-2xs inline-flex items-center gap-1"
                        title="View Full Prescription"
                      >
                        <FileText size={13} className="text-slate-500" />
                        <span>Rx</span>
                      </Link>

                      {/* View Appointment */}
                      {item.appointment?._id && (
                        <Link
                          to={`/appointments/${item.appointment._id}`}
                          className="px-2.5 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all shadow-2xs inline-flex items-center gap-1"
                          title="View Original Appointment"
                        >
                          <ExternalLink size={13} className="text-slate-500" />
                          <span>Apt</span>
                        </Link>
                      )}

                      {/* Update Status / Notes Modal */}
                      <button
                        onClick={() => openUpdateModal(item)}
                        className="px-2.5 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all shadow-2xs inline-flex items-center gap-1 cursor-pointer"
                        title="Update status or add follow-up notes"
                      >
                        <Edit3 size={13} />
                        <span>Status</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between bg-white px-5 py-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
              <span className="text-xs font-bold text-slate-500">
                Showing page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong> ({pagination.total} total follow-ups)
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.set('page', String(pagination.page - 1));
                    setSearchParams(nextParams);
                  }}
                  className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.set('page', String(pagination.page + 1));
                    setSearchParams(nextParams);
                  }}
                  className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 5. Update Status & Notes Modal ── */}
      {isUpdateModalOpen && selectedFollowUp && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-200 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-900">
                  Update Follow-up Status
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  {selectedFollowUp.lead?.patientName || selectedFollowUp.appointment?.patientName}
                </p>
              </div>
              <button
                onClick={() => setIsUpdateModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleStatusUpdate} className="space-y-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                  Follow-up Status
                </label>
                <select
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:bg-white focus:border-[#143E78] focus:outline-none"
                >
                  <option value="pending">Pending (In Queue)</option>
                  <option value="scheduled">Scheduled (Appointment Booked)</option>
                  <option value="completed">Completed (Follow-up Done)</option>
                  <option value="cancelled">Cancelled (Patient Not Required)</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                  Target Due Date
                </label>
                <input
                  type="date"
                  value={updateForm.targetDate}
                  onChange={(e) => setUpdateForm({ ...updateForm, targetDate: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:bg-white focus:border-[#143E78] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
                  Follow-up Notes / Call Outcome
                </label>
                <textarea
                  rows={3}
                  value={updateForm.notes}
                  onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                  placeholder="E.g., Called patient, patient reported recovery, next visit scheduled..."
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:bg-white focus:border-[#143E78] focus:outline-none resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-[#143E78] text-white hover:bg-[#0f2c56] rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50 cursor-pointer shadow-2xs"
                >
                  {submitting ? 'Saving...' : 'Save Updates'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentFollowUpList;
